import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ApiClient } from '../api/ApiClient';
import type { PublicCaptain } from '../api/types';
import styles from './PublicTeamPage.module.css';

const PAGE_LIMIT = 24;

const PLACEHOLDER_THUMBNAIL =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'>" +
      "<rect width='64' height='64' fill='#e9ecef'/>" +
      "<circle cx='32' cy='26' r='12' fill='#adb5bd'/>" +
      "<path d='M12 60c0-11 9-20 20-20s20 9 20 20' fill='#adb5bd'/>" +
      '</svg>',
  );

type Status = 'loading' | 'success' | 'error';

export default function PublicTeamPage(): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [items, setItems] = useState<PublicCaptain[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [activeDepartment, setActiveDepartment] = useState<string>('');
  const [allDepartments, setAllDepartments] = useState<string[]>([]);
  const [reloadToken, setReloadToken] = useState(0);

  // Fetch data
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    
    ApiClient.listPublicCaptains({ page, limit: PAGE_LIMIT, department: activeDepartment || undefined })
      .then((result) => {
        if (cancelled) return;
        setItems(Array.isArray(result.items) ? result.items : []);
        setTotal(typeof result.total === 'number' ? result.total : 0);
        setStatus('success');
      })
      .catch(() => {
        if (cancelled) return;
        setErrorMessage('تعذّر تحميل قائمة الفريق');
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [page, activeDepartment, reloadToken]);

  // Fetch departments for filter
  useEffect(() => {
    let cancelled = false;
    ApiClient.listPublicCaptains({ page: 1, limit: 100 })
      .then((result) => {
        if (cancelled) return;
        const depts = Array.from(
          new Set(
            result.items
              .map((c) => c.department ?? '')
              .filter((d) => d !== '')
          )
        ).sort();
        setAllDepartments(depts);
      })
      .catch(() => { /* silent */ });
      
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const totalPages = total > 0 ? Math.ceil(total / PAGE_LIMIT) : 1;
  const canGoPrev = page > 1;
  const canGoNext = page < totalPages;

  const handlePrev = useCallback(() => {
    setPage((c) => Math.max(1, c - 1));
  }, []);

  const handleNext = useCallback(() => {
    setPage((c) => c + 1);
  }, []);

  const handleDepartmentChange = useCallback((dept: string) => {
    setActiveDepartment(dept);
    setPage(1);
  }, []);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>فريق وصل لي</h1>
        <p className={styles.subtitle}>تعرف على شركاء النجاح</p>
      </header>

      {/* Filter Bar */}
      {allDepartments.length > 0 && (
        <div className={styles.filterBar}>
          <button
            type="button"
            className={activeDepartment === '' ? styles.filterActive : styles.filterBtn}
            onClick={() => handleDepartmentChange('')}
          >
            الكل
          </button>
          {allDepartments.map((dept) => (
            <button
              key={dept}
              type="button"
              className={activeDepartment === dept ? styles.filterActive : styles.filterBtn}
              onClick={() => handleDepartmentChange(dept)}
            >
              {dept}
            </button>
          ))}
        </div>
      )}

      {/* States */}
      {status === 'loading' && (
        <div className={styles.center}>
          <div className={styles.spinner} />
        </div>
      )}

      {status === 'error' && (
        <div className={styles.center}>
          <div className={styles.errorBox}>
            <p>{errorMessage}</p>
            <button type="button" className={styles.retryBtn} onClick={() => setReloadToken(r => r + 1)}>
              إعادة المحاولة
            </button>
          </div>
        </div>
      )}

      {status === 'success' && items.length === 0 && (
        <div className={styles.center}>
          <p style={{ color: 'rgba(255,255,255,0.5)' }}>لا يوجد أعضاء في هذا القسم حالياً</p>
        </div>
      )}

      {/* Grid */}
      {status === 'success' && items.length > 0 && (
        <>
          <div className={styles.grid}>
            {items.map((captain) => {
              // get primary title (legacy title or first element of jobTitles)
              const primaryTitle = Array.isArray(captain.jobTitles) && captain.jobTitles.length > 0 
                ? captain.jobTitles[0] 
                : captain.jobTitle;

              return (
                <Link to={`/captain/${captain._id}`} key={captain._id} className={styles.card}>
                  <div className={styles.imageWrapper}>
                    <img
                      src={captain.imageUrl || PLACEHOLDER_THUMBNAIL}
                      alt={captain.name}
                      className={styles.image}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).src = PLACEHOLDER_THUMBNAIL; }}
                    />
                  </div>
                  <h3 className={styles.name}>{captain.name}</h3>
                  <p className={styles.jobTitle}>{primaryTitle}</p>
                  
                  {captain.department && (
                    <span className={styles.department}>{captain.department}</span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Pagination */}
          {total > PAGE_LIMIT && (
            <div className={styles.pagination}>
              <button onClick={handlePrev} disabled={!canGoPrev} className={styles.pageBtn}>
                السابق
              </button>
              <span className={styles.pageInfo}>صفحة {page} من {totalPages}</span>
              <button onClick={handleNext} disabled={!canGoNext} className={styles.pageBtn}>
                التالي
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
